package com.shadowings.gero.domain.model

import com.shadowings.gero.data.repository.InscriptionImages

// Types never rebuilt into a correction question: MULTI_PRESS is never recorded in the first
// place (see MultiPressComposable.kt), and MULTI_SELECTION's "several independently toggled
// options" shape doesn't reduce to a reconstructible single answer (see MultiSelectionComposable.kt)
// — with only a handful of MULTI_SELECTION questions in the whole app, it's simply excluded from
// the mistakes list rather than special-cased.
private val UNRECORDED_TYPES = setOf(QuizQuestionType.MULTI_PRESS, QuizQuestionType.MULTI_SELECTION)

// Question types whose choices (options / word-bank tiles / Gardiner blocks) come from
// WrongAnswerRecord.options.
private val CHOICE_TYPES = setOf(
    QuizQuestionType.SINGLE_SELECTION,
    QuizQuestionType.WORD_SELECTION,
    QuizQuestionType.WORD_SPELLING,
)

// A choice-type record is only replayable if it has saved options. That rules out records saved
// before WrongAnswerRecord.options existed: there's no faithful way to rebuild their distractors,
// and replaying them with only the correct tiles/blocks would be a giveaway — worse, getting one
// wrong during correction would re-record it with those correct-only options, making it look
// complete forever. They come back, with options, the next time the user misses that question in
// its lesson (recordWrongAnswer upserts the whole record). SINGLE_SELECTION additionally needs the
// correct answer among its options, which rules out the defensive multi-correct case whose
// correctAnswerText is a positional description ("erano la prima e la terza"), not an option.
private fun WrongAnswerRecord.isReplayable(): Boolean = when (type) {
    in UNRECORDED_TYPES -> false
    QuizQuestionType.SINGLE_SELECTION -> correctAnswerText in options
    in CHOICE_TYPES -> options.isNotEmpty()
    // questionText holds the drawable code: an image removed from the app can't be replayed.
    QuizQuestionType.IMAGE_KEYBOARD -> InscriptionImages.resFromCode(questionText) != null
    else -> true
}

// Used both to filter the pool a correction batch is sampled from (so a batch of N picks N
// records that will actually turn into a question) and inside buildCorrectionQuizContent itself,
// as a defensive filter against any leftover non-replayable record.
fun List<WrongAnswerRecord>.correctable(): List<WrongAnswerRecord> = filter { it.isReplayable() }

/**
 * Rebuilds a "Correggi" quiz session from saved mistakes. Each [WrongAnswerRecord] carries the
 * choices the user was originally shown ([WrongAnswerRecord.options]), so this just replays them
 * (reshuffled) rather than reloading lesson content or inventing distractors.
 */
fun buildCorrectionQuizContent(records: List<WrongAnswerRecord>): List<IQuizContent> =
    records.correctable().map(::buildQuizContent)

private fun buildQuizContent(record: WrongAnswerRecord): IQuizContent =
    when (record.type) {
        QuizQuestionType.TRUE_FALSE -> TrueFalseContent(
            text = record.questionText,
            correct = record.correctAnswerText.toBooleanStrictOrNull() ?: false
        )

        QuizQuestionType.TEXT_TRANSLATION -> TextTranslationContent(
            hieroglyphs = record.hieroglyphs,
            correctPhrase = record.correctAnswerText,
            soggettiCount = record.subjectsCount,
            oggettiCount = record.objectsCount
        )

        QuizQuestionType.SINGLE_SELECTION -> SingleSelectionContent(
            text = record.questionText,
            detail = record.hieroglyphs,
            options = record.options.distinct().shuffled()
                .map { Option(it, correct = it == record.correctAnswerText) }
        )

        QuizQuestionType.WORD_SELECTION -> WordSelectionContent(
            hieroglyphs = record.hieroglyphs,
            correctPhrase = record.correctAnswerText,
            words = record.options.shuffled(),
            soggettiCount = record.subjectsCount,
            oggettiCount = record.objectsCount,
            alternativePhrases = record.alternativeAnswers
        )

        QuizQuestionType.WORD_SPELLING -> WordSpellingContent(
            prompt = record.questionText,
            gardiner = record.hieroglyphs,
            blocks = record.options.shuffled()
        )

        // reading is stored one character per element (see VocabUtils.buildVocabQuizContent),
        // then joined with "" into correctAnswerText — splitting it back into single characters
        // is a lossless reversal, not an approximation.
        QuizQuestionType.PHONETIC_KEYBOARD -> PhoneticKeyboardContent(
            sentence = record.hieroglyphs,
            reading = record.correctAnswerText.map { it.toString() },
            info = ""
        )

        // Same one-character-per-element reading as PHONETIC_KEYBOARD. The translation isn't
        // stored, so the replayed question's feedback has no info, like PHONETIC_KEYBOARD's.
        QuizQuestionType.IMAGE_KEYBOARD -> ImageKeyboardContent(
            imageCode = record.questionText,
            reading = record.correctAnswerText.map { it.toString() }
        )

        QuizQuestionType.MULTI_PRESS, QuizQuestionType.MULTI_SELECTION ->
            error("${record.type} mistakes are never recorded")
    }
